export const GA4_MEASUREMENT_ID = 'G-WH0H80V5V6';
const POSTHOG_KEY = 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
const POSTHOG_HOST = 'https://us.i.posthog.com';
export const CONSENT_STORAGE_KEY = 'eldera_cookie_consent';
export const CONSENT_VERSION = 'v1-2026-06';
export const CONSENT_EVENT = 'eldera:consent';
export const OPEN_SETTINGS_EVENT = 'eldera:cookie-settings';

const isBrowser = typeof window !== 'undefined';
const isPrerender = isBrowser && /ReactSnap/.test(navigator.userAgent);

let gaLoaded = false;
let lastTrackedPage = null;
let lastPosthogPage = null;
let currentPage = null;

export const getConsent = () => {
  if (!isBrowser) return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.version === CONSENT_VERSION ? parsed : null;
  } catch (e) {
    return null;
  }
};

export const hasAnalyticsConsent = () => getConsent()?.analytics === 'granted';

function gtag() {
  window.dataLayer = window.dataLayer || [];
  // gtag.js only treats `arguments` objects (not arrays) as commands.
  window.dataLayer.push(arguments);
}

// GA4 DebugView for authorised testing sessions only: open any page once with
// ?ga_debug=<GA_DEBUG_TOKEN>; the flag is kept in sessionStorage for that tab.
// Effect limited to debug_mode:true (events appear in DebugView); no data exposure.
const GA_DEBUG_TOKEN = 'eldera-qa-7f3a9c';
const isDebugSession = () => {
  try {
    const m = window.location.href.match(/[?&#]ga_debug=([^&#]+)/);
    if (m) {
      if (decodeURIComponent(m[1]) === GA_DEBUG_TOKEN) sessionStorage.setItem('eldera_ga_debug', '1');
      else sessionStorage.removeItem('eldera_ga_debug');
    }
    return sessionStorage.getItem('eldera_ga_debug') === '1';
  } catch (e) {
    return false;
  }
};

// Native history methods captured before gtag.js loads. gtag's Enhanced Measurement
// wraps pushState/replaceState to emit its own page_view on SPA navigation; we restore
// the native methods after load so only our single manual page_view per route is sent.
const nativeHistory = isBrowser
  ? { pushState: window.history.pushState, replaceState: window.history.replaceState }
  : null;

const restoreNativeHistory = () => {
  if (!nativeHistory) return;
  window.history.pushState = nativeHistory.pushState;
  window.history.replaceState = nativeHistory.replaceState;
};

// GA Enhanced Measurement also listens to popstate (back/forward). Our listener is
// registered after React Router's (app already mounted) but before gtag.js, so
// stopImmediatePropagation only silences listeners added later (gtag, PostHog).
let popstateGuardInstalled = false;
const installPopstateGuard = () => {
  if (popstateGuardInstalled) return;
  popstateGuardInstalled = true;
  window.addEventListener('popstate', (e) => e.stopImmediatePropagation());
};

let gtagScriptInjected = false;
// Called once the app is mounted (page effect or user click), never at module load.
const injectGtagScript = () => {
  if (gtagScriptInjected || !gaLoaded || isPrerender) return;
  gtagScriptInjected = true;
  installPopstateGuard();
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  s.setAttribute('data-analytics', 'ga4');
  s.onload = () => { restoreNativeHistory(); setTimeout(restoreNativeHistory, 1500); };
  document.head.appendChild(s);
};

const loadGa4 = () => {
  if (gaLoaded || isPrerender) return;
  gaLoaded = true;
  window.gtag = gtag;
  window[`ga-disable-${GA4_MEASUREMENT_ID}`] = false;
  gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  gtag('js', new Date());
  const config = { send_page_view: false, anonymize_ip: true };
  if (isDebugSession()) config.debug_mode = true;
  gtag('config', GA4_MEASUREMENT_ID, config);
};

const loadPostHog = () => {
  if (isPrerender) return;
  if (window.posthog?.__loaded) {
    if (window.posthog.has_opted_out_capturing?.()) window.posthog.opt_in_capturing();
    return;
  }
  import('posthog-js').then(({ default: posthog }) => {
    if (!hasAnalyticsConsent()) return;
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only',
      capture_pageview: false,
      session_recording: { recordCrossOriginIframes: true, capturePerformance: false },
    });
    window.posthog = posthog;
    posthogPageView(currentPage);
  });
};

const enableAnalytics = () => {
  window[`ga-disable-${GA4_MEASUREMENT_ID}`] = false;
  loadGa4();
  gtag('consent', 'update', { analytics_storage: 'granted' });
  loadPostHog();
};

const posthogPageView = (page) => {
  const ph = window.posthog;
  if (!page || !ph?.__loaded || ph.has_opted_out_capturing?.() || lastPosthogPage === page.key) return;
  lastPosthogPage = page.key;
  ph.capture('$pageview', { $current_url: page.location, title: page.title });
};

// AI search engines as referrers (session-scoped, no personal data). Lets GA4 report
// visits coming from ChatGPT, Perplexity, Copilot, Gemini or Claude via a custom dimension.
const AI_REFERRERS = [
  [/(^|\.)chatgpt\.com$|(^|\.)openai\.com$/, 'chatgpt'],
  [/(^|\.)perplexity\.ai$/, 'perplexity'],
  [/(^|\.)copilot\.microsoft\.com$|(^|\.)bing\.com$/, 'copilot_bing'],
  [/(^|\.)gemini\.google\.com$|(^|\.)bard\.google\.com$/, 'gemini'],
  [/(^|\.)claude\.ai$/, 'claude'],
];
const getAiSource = () => {
  try {
    const stored = sessionStorage.getItem('eldera_ai_source');
    if (stored) return stored;
    const ref = document.referrer ? new URL(document.referrer).hostname : '';
    const hit = AI_REFERRERS.find(([re]) => re.test(ref));
    const value = hit ? hit[1] : 'none';
    sessionStorage.setItem('eldera_ai_source', value);
    return value;
  } catch (e) {
    return 'none';
  }
};

const sendPageView = (page) => {
  if (!page) return;
  posthogPageView(page);
  if (lastTrackedPage === page.key) return;
  lastTrackedPage = page.key;
  gtag('event', 'page_view', {
    page_path: page.path,
    page_location: page.location,
    page_title: page.title,
    ai_source: getAiSource(),
  });
};

export const trackPageView = ({ path, search = '', title }) => {
  if (!isBrowser || isPrerender) return;
  const cleanSearch = search.replace(/([?&])ga_debug=[^&]*&?/, '$1').replace(/[?&]$/, '');
  const key = `${path}${cleanSearch}`;
  currentPage = { key, path: key, location: `${window.location.origin}${key}`, title };
  if (hasAnalyticsConsent()) {
    loadGa4();
    injectGtagScript();
    sendPageView(currentPage);
  }
};

// Business events (no personal data). Sent only with analytics consent.
export const trackEvent = (name, params = {}) => {
  if (!isBrowser || isPrerender || !hasAnalyticsConsent()) return;
  loadGa4();
  injectGtagScript();
  gtag('event', name, params);
  const ph = window.posthog;
  if (ph?.__loaded && !ph.has_opted_out_capturing?.()) ph.capture(name, params);
};

const purgeAnalyticsStorage = () => {
  const host = window.location.hostname;
  document.cookie.split(';').forEach((c) => {
    const name = c.split('=')[0].trim();
    if (/^(_ga|_gid|_gat|ph_)/.test(name)) {
      [host, `.${host}`, `.${host.split('.').slice(-2).join('.')}`].forEach((d) => {
        document.cookie = `${name}=; Max-Age=0; path=/; domain=${d}`;
      });
      document.cookie = `${name}=; Max-Age=0; path=/`;
    }
  });
  try {
    Object.keys(localStorage).filter((k) => /^ph_/.test(k)).forEach((k) => localStorage.removeItem(k));
  } catch (e) { /* noop */ }
};

const disableAnalytics = () => {
  window[`ga-disable-${GA4_MEASUREMENT_ID}`] = true;
  if (gaLoaded) gtag('consent', 'update', { analytics_storage: 'denied' });
  if (window.posthog?.__loaded) {
    window.posthog.opt_out_capturing();
    window.posthog.stopSessionRecording?.();
  }
  purgeAnalyticsStorage();
  // PostHog persists its state asynchronously; purge again after it settles.
  setTimeout(purgeAnalyticsStorage, 1500);
};

export const setConsent = (analyticsGranted) => {
  if (!isBrowser) return;
  const value = {
    analytics: analyticsGranted ? 'granted' : 'denied',
    version: CONSENT_VERSION,
    date: new Date().toISOString(),
  };
  try { localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(value)); } catch (e) { /* noop */ }
  if (analyticsGranted) {
    enableAnalytics();
    injectGtagScript();
    sendPageView(currentPage);
  } else {
    disableAnalytics();
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
};

export const initAnalyticsFromStoredConsent = () => {
  if (!isBrowser || isPrerender) return;
  isDebugSession();
  if (!hasAnalyticsConsent()) {
    if (getConsent()?.analytics === 'denied') purgeAnalyticsStorage();
    return;
  }
  enableAnalytics();
};

export const openCookieSettings = () => {
  if (isBrowser) window.dispatchEvent(new CustomEvent(OPEN_SETTINGS_EVENT));
};
